import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/styles'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import UpcomingIcon from 'components/common/icons/Calendar2'
import PastIcon from 'components/common/icons/Calendar3'
import AppointmentRow from './AppointmentRow'
import { coerceApptList } from './greyfinchFormat'

const BASE_KEY = 'pages.patients.patientSidebar.items.greyfinch'

const DARK_COLOR = '#001335'
const MUTED_COLOR = '#727B8C'
const DIVIDER_COLOR = '#E6E6E6'

const useStyles = makeStyles(() => ({
  section: {
    marginTop: 24
  },
  subLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10
  },
  icon: {
    flex: '0 0 auto',
    width: 14,
    height: 15
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  // The upcoming block is closed by a bottom rule and the past block opened by a top rule; the gap
  // between them renders the two-line divider from the design.
  upcoming: {
    marginTop: 12,
    paddingBottom: 16,
    borderBottom: `1px solid ${DIVIDER_COLOR}`
  },
  past: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${DIVIDER_COLOR}`
  }
}))

const AppointmentsSection = ({ upcoming, past }) => {
  const { t } = useTranslation()
  const classes = useStyles()

  // Coerce to arrays before .length/.map: the backend's mapPatientSummary always emits arrays, so a
  // non-array here is contract drift (or a future pagination envelope), not an expected vendor shape.
  // coerceApptList logs that drift rather than silently rendering an affirmative "no appointments"; it also
  // prevents the throw that — with no error boundary in the app — would white-screen the patient page.
  const upcomingList = coerceApptList(upcoming, 'upcoming')
  const pastList = coerceApptList(past, 'past')

  return (
    <div className={classes.section}>
      <DazzedParagraph14 bold color={DARK_COLOR}>
        {t(`${BASE_KEY}.appointments.title`)}
      </DazzedParagraph14>

      <div className={classes.upcoming}>
        <div className={classes.subLabel}>
          <UpcomingIcon className={classes.icon} color={MUTED_COLOR} />
          <DazzedParagraph14 strong color={MUTED_COLOR}>
            {t(`${BASE_KEY}.appointments.upcoming`)}
          </DazzedParagraph14>
        </div>
        {upcomingList.length ? (
          <div className={classes.rows}>
            {upcomingList.map((appointment, index) => (
              <AppointmentRow key={index} appointment={appointment} />
            ))}
          </div>
        ) : (
          <DazzedParagraph14 color={MUTED_COLOR}>{t(`${BASE_KEY}.states.noUpcoming`)}</DazzedParagraph14>
        )}
      </div>

      <div className={classes.past}>
        <div className={classes.subLabel}>
          <PastIcon className={classes.icon} color={MUTED_COLOR} />
          <DazzedParagraph14 strong color={MUTED_COLOR}>
            {t(`${BASE_KEY}.appointments.past`)}
          </DazzedParagraph14>
        </div>
        {pastList.length ? (
          <div className={classes.rows}>
            {pastList.map((appointment, index) => (
              <AppointmentRow key={index} appointment={appointment} muted />
            ))}
          </div>
        ) : (
          <DazzedParagraph14 color={MUTED_COLOR}>{t(`${BASE_KEY}.states.noPast`)}</DazzedParagraph14>
        )}
      </div>
    </div>
  )
}

export default AppointmentsSection
